import { FrameworkContext } from "global/cnr/contexts/FrameworkContent";
import { ParentContext } from "global/cnr/contexts/ParentContext";
import { useAppUserAccess } from "global/cnr/contexts/useHelpers/useAppUserAccess";
import { useLongPress } from "global/cnr/contexts/useHelpers/useLongPress";
import { useSwiperControl } from "global/cnr/contexts/useHelpers/useSwiperControl";
import FullImage from "global/components/imaging/FullImage";
import { gEnums } from "global/enums/globalEnums";
import React, { createContext, useContext, useMemo, useState } from "react";
import LongPressModal from "./LongPressModal";

const _allow_appUserLongPressDirect = true
const _show_OptionsFromLongPress = true

export const LongPressContext = createContext();

export const _longPressTypes = {
  appUser: 'appUser',
  bannerImage: 'bannerImage',
  default: 'default',
  header: 'header',
  image_grid: 'image_grid',
  image: 'image',
  item: 'item',
  match: 'match',
  tab: 'tab',
}

export const LongPressWrapper = ({
  children,
  onClick,
  pressTime = 600,
  moveTolerance = 15,
  longPressType = _longPressTypes.item,
  itemData,
  hasImage = false,
  handleOnPress = null,
  longPressModal,
  opts,
  isholding = false,
}) => {

  const appUserAccess = useAppUserAccess()
  const { isAdminOrSuper } = appUserAccess ?? {}

  const parentContext = useContext(ParentContext);
  const { states } = parentContext ?? {}
  const { currentSettings_state } = states

  const fraweworkContext = useContext(FrameworkContext)
  const { framework_handlers } = fraweworkContext ?? {}

  const { aps_global } = _.get(currentSettings_state, 'gvviSettings', {});
  const { appSettings } = aps_global ?? {}
  const { showOptionsFromLongPress, allowAppUserLongPressDirect } = appSettings ?? {}

  const _allowAppUserLongPressDirect = isAdminOrSuper || allowAppUserLongPressDirect || _allow_appUserLongPressDirect
  const _showOptionsFromLongPress = isAdminOrSuper || showOptionsFromLongPress || _show_OptionsFromLongPress

  const _pressTime = useMemo(() => {
    switch (longPressType) {
      case _longPressTypes.appUser:
        return _allowAppUserLongPressDirect ? 0 : pressTime;
      default:
        return pressTime
    }
  }, [longPressType])


  const frameworkContext = useContext(FrameworkContext)
  const { framework_state } = frameworkContext ?? {}
  const { view, fw_info } = framework_state ?? {}
  const { uivi } = fw_info ?? {}

  const { lockSwiper, unlockSwiper } = useSwiperControl()

  const [showItemModal, setShowItemModal] = useState()
  const [showItemImage, setShowItemImage] = useState()

  const handleShowItemModal = () => setShowItemModal(!showItemModal)
  const handleShowItemImage = () => setShowItemImage(!showItemImage)

  const access = useMemo(() => {
    if (!appUserAccess) { return {} }
    const _access = {
      appProfile: appUserAccess.accessLevel >= gEnums.allAuthLevels.appProfile.value,
      auth: appUserAccess.accessLevel >= gEnums.allAuthLevels.superAdmin.value,
      data: appUserAccess.accessLevel >= gEnums.allAuthLevels.appDataAdmin.value,
      settings: appUserAccess.accessLevel >= gEnums.allAuthLevels.appAdmin.value,
    }
    switch (longPressType) {
      case _longPressTypes.appUser:
        _access.any = _access.auth || _access.data || _access.settings || _access.appProfile
        break;
      default:
        _access.any = _access.auth || _access.data || _access.settings
    }
    return _access
  }, [appUserAccess, longPressType])

  const ignoreOverlay = useMemo(() => {
    switch (longPressType) {
      case _longPressTypes.appUser:
      case _longPressTypes.match:
        return false
      case _longPressTypes.item:
      case _longPressTypes.tab:
      case _longPressTypes.header:
      case _longPressTypes.image_grid:
      case _longPressTypes.bannerImage:
      case _longPressTypes.image:
      case _longPressTypes.bannerImage:
        return true
    }
  }, [longPressType])

  const handlePress = (e) => {
    if (!_showOptionsFromLongPress || !access.any) return;
    lockSwiper && lockSwiper()
    // if handleOnPress is a prop, trigger that.
    if (handleOnPress) {
      handleOnPress()
    } else {
      framework_handlers.handleSet_currentLongPress(
        {
          longPressType,
          view: view,
          viewKey: fw_info?.viewKey,
          uivi: uivi,
          uiviKey: itemData?._itemKey,
          longPressData: itemData
        })
      switch (longPressType) {
        case _longPressTypes.appUser:
        case _longPressTypes.bannerImage:
        case _longPressTypes.header:
        case _longPressTypes.image_grid:
        case _longPressTypes.item:
        case _longPressTypes.match:
        case _longPressTypes.tab:
          handleShowItemModal()
          break;
        case _longPressTypes.image:
        case _longPressTypes.bannerImage:
          handleShowItemImage()
          break;
        // case _longPressTypes.match:
        // framework_handlers.handleShow_appBottomModal(longPressModal)
        // break;
      }
    }
  }

  const handleClick = (e) => {
    unlockSwiper && unlockSwiper()
    onClick && onClick(e)
  }

  const handleRelease = (e) => {
    unlockSwiper && unlockSwiper()
  }

  const longPress = useLongPress(
    handlePress,
    handleClick,
    _pressTime,
    moveTolerance,
    longPressType,
    handleRelease
  );

  const appImage = () => {
    const { appImage } = opts ?? {}
    return <LongPressContext.Provider value={{ longPress_state: { appImage, longPressType, directView: view, directViewItem: uivi }, longPress_handlers: { handleCancel: handleShowItemImage } }}>
      <FullImage _appImage={appImage} handleClose={handleShowItemImage} />
    </LongPressContext.Provider>
  }

  return (
    <>
      {showItemModal && (
        <LongPressContext.Provider value={{ longPress_state: { longPressType, directView: view, directViewItem: uivi }, longPress_handlers: { handleCancel: handleShowItemModal } }}>
          <LongPressModal
            itemData={itemData}
            hasImage={hasImage}
            handleCancel={handleShowItemModal}
            longPressType={longPressType}
            longPressModal={longPressModal}
            isOverlay={!ignoreOverlay}
          />
        </LongPressContext.Provider>
      )}

      {showItemImage && (
        appImage()
      )}

      {React.cloneElement(children, {
        ...children.props,
        ...longPress,
        className: [
          children.props?.className,
          longPress?.className,
          longPress?.isHolding && ' lpwh'
        ].filter(Boolean).join(" "),
        style: {
          ...(children.props?.style ?? {}),
          ...(longPress?.style ?? {})
        }
      })}
    </>
  );
}; 